// src/pos-app/surfaces/cashier/SellSurface.js
//
// F12a-N Task 1 — native Cashier Sell surface skeleton (design contract:
// docs/mockups/f12a-n/cashier-sell.html). Mounted by CashierStation's Sell
// tab when SURFACES.cashier === 'native' (surfaces/config.js).
//
// Layout frame (HARD no-scroll rule, spec §6): a flex column that fills the
// station's content slot exactly —
//   [bill-request strip slot (Task 4)]                    flex:none
//   [dining-pill row]                                     flex:none
//   [stage: content zone | cart column 348px]             flex:1 min-height:0
// Nothing page-scrolls; only interior panes (cart body, future grids) may
// scroll internally.
//
// F15 — at phone width (<=768px) the frame gains a fourth row and the cart
// column leaves the stage entirely:
//   [bill-request strip slot]                             flex:none
//   [dining-pill row]                                     flex:none
//   [stage: content zone, FULL width]                     flex:1 min-height:0
//   [cart bar: count + total + open]                      flex:none
// plus a scrim + slide-up sheet hosting the same CartPanel, both
// position:absolute against .slf-root. The no-scroll rule is unchanged:
// .slf-root is the containing block AND overflow:hidden, so the overlay pair
// is bounded by the surface and still nothing page-scrolls.
//
// Business logic is REUSED, never forked: dining options via useDiningSetup,
// session gating via useSessionManager + useBusinessDay exactly as the
// legacy CashierSalesPage does (see its lines 1793–1819), and the reused
// <StartOfDayScreen/> fills the content slot when no session is active.
//
// Task 2: browse mode hosts <CatalogBrowse/>. SellSurface owns the shared
// useProductCatalog + useOrderBuilder instances (the Task 3 cart panel reads
// the same builder), the add-to-cart entry point (legacy handleAddProduct,
// CashierSalesPage.js:707-716 — dining-pill nudge when no option is picked),
// and the transient error toast the builder's stock-limit guard writes to.
// 'tables' mode still renders a placeholder zone — real grid in Task 4.
//
// Task 3: the cart column is <CartPanel/>. SellSurface also owns the shared
// usePromoEvaluator instance — mirroring the legacy page-level composition
// (CashierSalesPage.js:215-230) so the Task 5 charge path reads the same
// applied promo the cart displays — and the no-table daily-order-number
// preview on pill select (legacy handleDiningPillSelect, 687-703). The
// editing-conflict guard from the legacy pill handler arrives with Task 6.
//
// Task 4: 'tables' mode hosts <TableSelect/> (List / Visual / random-number).
// SellSurface owns the page-level pieces the legacy page owned:
// usePendingOrders (shared with the Task 6 orders panel) fed by the faithful
// service-charge recalculation (legacy 350-378), the 10s occupation poll
// with the seenBillRequests diff (legacy 497-534), and the amber
// bill-request banner strip (legacy 2521-2568). handleOpenOrder is a stub
// until Task 6 wires the real order-detail modal.
//
// Task 5 (MONEY — the Charge path): onCharge builds the no-table immediate
// sale transaction via lib/transactionFlow.buildTransaction (faithful
// extraction of legacy handleImmediateSaleCreation, CashierSalesPage.js:
// 1052-1095) and opens the REUSED PaymentConfirmModal (zero edits). Confirm
// routes into lib/transactionFlow.processTransactionWithPayment — the
// faithful extraction of the legacy function of the same name (1231-1486),
// including its "Split Payment" branch which hands off to the REUSED
// SplitBillManager (imperative ref, composed with the exact prop set legacy
// passes at CashierSalesPage.js:3064-3128). Attribution comes from
// useCashierDetection (also fixes the Task-1 Minor: the SoD screen's
// employeeName now prefers cashierName, matching legacy). Charging a
// table-service option (Hold/settle-from-pending) is explicitly out of
// scope — Task 6.
//
// Task N (user-QC finding 3 — consolidate Orders into ONE surface):
// SellSurface accepts `ordersTabActive` (bool, from CashierStation's active
// bottom tab) + `onRequestOrdersTab`/`onRequestSellTab` callbacks. The Sell
// content (bill banners/pills/stage) is CSS-hidden (not unmounted — same
// display-toggle trick CashierStation already uses for its own Sell pane)
// while `ordersTabActive` is true; `OrdersPanelNative` is now ALWAYS
// mounted (as before) but rendered `embedded` with `isOpen={ordersTabActive}`
// instead of a locally-owned `ordersPanelOpen` slide-in flag — there is no
// second way to reach it any more (the top-bar Orders button is gone, see
// TopBarExtras.js). `handleOpenOrder` (existing-table tap / bill-banner
// "View order") now calls `onRequestOrdersTab()` instead of flipping local
// state, and the embedded panel's `onClose` (fired after Edit/Change
// Table/Settle, its existing contract) calls `onRequestSellTab()` so the
// cashier lands back on Sell to see the builder it just loaded/affected.
//
// Task N (user-QC finding 4 — error/success toast): both toasts now
// auto-dismiss after 2.5s via a generic effect keyed off the message value
// (clears + restarts on a new message, cleans up its timer on unmount) —
// legacy CashierSalesPage.js is inconsistent here (2000-3000ms across call
// sites, and `charge_requires_hold_first` specifically has NO timeout at
// all, which is the literal source of the "sticks forever" bug report);
// this surface converges on a single 2.5s rule everywhere instead. The
// toast row also moved from a `position:absolute` overlay pinned to the top
// of the surface (which sat on top of the dining pills) into NORMAL FLOW as
// the very first child of the root, ABOVE the pills/bill-banners — same
// pattern legacy's own `<Alert>` uses (`flex-shrink:0`, in-flow, not a
// floating overlay). That guarantees zero overlap by construction rather
// than by picking a "hopefully clear" floating position, which is safer
// under this surface's fixed 348px cart column + wrapping pill row.
//
// Task 7 (shell extras + session flows): SellSurface publishes a snapshot
// (session id/date, pending-orders count, printing-pref toggles) plus a
// stable action-callback ref into the TopBarExtras context bridge (see
// TopBarExtras.js header for the wiring decision) — CashierStation mounts
// <TopBarExtras/> in StationTopBar's children slot and the single hook
// instances stay HERE. The More-menu actions land back in this file:
// Staff Allowance pulses the REUSED StaffAllowanceManager's externalOpen
// (legacy CashierSalesPage.js:2264-2276 prop set + the 2347 pulse trick),
// the Kitchen Printing / Receipt Translations toggles are faithful copies
// of legacy handlePrintingToggle (719-729) / handleReceiptTranslationsToggle
// (575-597) writing the SAME tenantStorage keys, and End Session sets
// showEoD → the REUSED EndOfDayFlow fills the content slot (legacy
// 1809-1819). EOD completion → handleSessionClosed → AuthContext.logout()
// (capacitor branch preserves the tenant binding) → CashierStation's
// lock-on-logout effect locks the operator. No printer-warning banner on
// this surface: printing always routes over the network, and there is no
// local print path on any host to warn about (asserted in tests).
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage, getEntityTranslation, translateDynamic } from '../../../context/LanguageContext';
import useDiningSetup from '../../../hooks/pos/useDiningSetup';
import useBusinessDay from '../../../hooks/pos/useBusinessDay';
import useSessionManager from '../../../hooks/pos/useSessionManager';
import useProductCatalog from '../../../hooks/pos/useProductCatalog';
import useOrderBuilder from '../../../hooks/pos/useOrderBuilder';
import usePromoEvaluator from '../../../hooks/pos/usePromoEvaluator';
import usePendingOrders from '../../../hooks/pos/usePendingOrders';
import useCashierDetection from '../../../hooks/pos/useCashierDetection';
import useIsMobile from '../../../hooks/useIsMobile';
import { occupationService, paymentMethodService, transactionService, modifierService } from '../../../services/api';
import tenantStorage from '../../../services/tenantStorage';
import StartOfDayScreen from '../../../components/pos/StartOfDayScreen';
import EndOfDayFlow from '../../../components/pos/EndOfDayFlow';
import StaffAllowanceManager from '../../../components/pos/StaffAllowanceManager';
import ManagerGate from '../../../components/pos/ManagerGate';
import { kickDrawerManually } from '../../../utils/drawerKick';
import { canTranslateReceipts, resolveReceiptLanguage } from '../../../utils/receiptLanguage';
import SplitBillManager from '../../../components/pos/SplitBillManager';
import PaymentConfirmModal from '../../../components/pos/modals/PaymentConfirmModal';
import EditingProtectionModal from '../../../components/pos/modals/EditingProtectionModal';
import ChangeTableModal from '../../../components/pos/modals/ChangeTableModal';
import CatalogBrowse from './CatalogBrowse';
import { usePublishSellExtras, useSellExtrasMenu } from './TopBarExtras';
import CartPanel from './CartPanel';
import { money, cartItemCount } from './lib/money';
import TableSelect from './TableSelect';
import OrdersPanelNative from './OrdersPanelNative';
import { buildTransaction, processTransactionWithPayment } from './lib/transactionFlow';
import {
    createPendingOrder,
    createPaidOrder,
    clearPaidOrder,
    loadOrderForEdit,
    saveEditedOrder,
    prepareOrderForPayment,
    changeOrderTable,
} from './lib/pendingOrderFlow';

// Tokens only (--c-* theme vars, light + dark). Logical properties keep the
// frame RTL-correct; all tap targets are ≥44px.
const CSS = `
.slf-root{flex:1;height:100%;min-height:0;display:flex;flex-direction:column;overflow:hidden;box-sizing:border-box;position:relative;}

/* bill-request notification strip — mockup .notify (amber banner) */
.slf-notify{flex:none;display:flex;align-items:center;gap:10px;padding:10px 14px;margin:12px 16px 0;
  background:var(--c-amberLight);border:1px solid var(--c-amberBorder);border-radius:13px;
  color:var(--c-ink);font-size:13px;font-weight:600;}
.slf-notify svg{color:var(--c-amber);flex:none;}
.slf-notify .grow{flex:1;min-width:0;}
.slf-notify-view{position:relative;height:32px;padding:0 12px;border-radius:9px;cursor:pointer;font-family:inherit;
  border:1px solid var(--c-amberBorder);background:var(--c-surface);color:var(--c-amber);font-weight:700;font-size:12.5px;}
.slf-notify-view::after{content:"";position:absolute;inset:-7px;}
.slf-notify-x{position:relative;width:28px;height:28px;flex:none;border:none;border-radius:8px;cursor:pointer;
  background:none;color:var(--c-amber);display:flex;align-items:center;justify-content:center;}
.slf-notify-x::after{content:"";position:absolute;inset:-9px;}

/* tables zone — hosts TableSelect (its grid is the only scroller) */
.slf-tables{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}

/* Sell content wrapper (Task N finding 3) — CSS-hidden, not unmounted,
   while the Orders tab is active (same display-toggle CashierStation uses
   for its own Sell pane), so catalog/table-select/cart state survives a
   tab hop. Wraps the pre-existing bill-notify/pills/stage flex column. */
.slf-sell-content{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}

/* transient error/success toast row (Task N finding 4) — NORMAL FLOW, not a
   floating overlay: renders as the root's first child, above the pills/
   bill-banners, so it can never cover them (previously position:absolute
   top:12px, which did). */
.slf-toast-row{flex:none;display:flex;justify-content:center;padding:0 16px;margin-top:10px;gap:8px;}
.slf-toast{width:fit-content;max-width:100%;padding:10px 16px;border-radius:12px;font-size:12.5px;font-weight:700;
  background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-redText);
  box-shadow:0 4px 14px rgba(2,8,23,.1);}
.slf-toast.success{background:var(--c-greenLight);border-color:var(--c-greenBorder);color:var(--c-greenDark);}

/* SoD / locked states fill the whole surface (interior scroll only) */
.slf-fill{flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;}
.slf-locked{flex:1;min-height:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:8px;text-align:center;padding:24px;color:var(--c-muted);}
.slf-locked-ic{width:56px;height:56px;border-radius:18px;margin-bottom:6px;display:flex;align-items:center;justify-content:center;background:var(--c-faint);border:1px solid var(--c-border);color:var(--c-muted);}
.slf-locked-h{font-size:16px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);margin:0;}
.slf-locked-p{font-size:13px;font-weight:600;color:var(--c-muted);margin:0;max-width:340px;line-height:1.5;}

/* dining pills — mockup .pills / .pill */
.slf-pills{flex:none;display:flex;gap:8px;flex-wrap:wrap;padding:14px 16px 0;border-radius:14px;transition:box-shadow .15s;}
/* 1.2s nudge pulse when adding without a dining option (legacy 707-716 + 2577) */
.slf-pills.nudge{box-shadow:0 0 0 2px var(--c-green);}
.slf-pill{display:inline-flex;align-items:center;gap:7px;min-height:44px;padding:0 16px;border-radius:999px;
  background:var(--c-surface);border:1px solid var(--c-border);cursor:pointer;font-family:inherit;
  font-size:13.5px;font-weight:700;color:var(--c-slate);box-shadow:0 1px 2px rgba(2,8,23,.05);
  transition:border-color .12s, box-shadow .12s;}
.slf-pill:hover{border-color:var(--c-greenBorder);}
.slf-pill.active{background:var(--c-green);border-color:var(--c-green);color:#fff;
  box-shadow:0 4px 14px color-mix(in srgb, var(--c-green) 35%, transparent);}

/* stage — mockup .stage (content | 348px cart) */
.slf-stage{flex:1;min-height:0;display:flex;}
.slf-content{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;overflow:hidden;padding:14px 16px 12px;}

/* browse zone — hosts CatalogBrowse (its grid is the only scroller) */
.slf-browse{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}

/* ── F15: phone width (<=768px) ──────────────────────────────────────────
   The cart leaves the stage row entirely below the breakpoint and becomes a
   persistent bar + slide-up sheet. Nothing here applies above 768px, so the
   tablet layout above is untouched.

   Why the cart has to LEAVE the row rather than shrink: .crt-root is a fixed
   348px (300px under 980px). A 1080x2400 phone is ~393 CSS px, so the content
   zone was left ~93px — below CatalogBrowse's minmax(148px,1fr) track, which
   collapsed the product grid to one clipped column. With the cart gone the
   existing auto-fill rule yields two real columns unaided; the grid needs no
   changes at all.

   The scrim and sheet are absolutely positioned against .slf-root (the only
   positioned ancestor, and already overflow:hidden), so they stay INSIDE the
   surface — the HARD no-scroll rule in this file's header still holds. */
.slf-cartbar{flex:none;display:flex;align-items:center;gap:12px;width:100%;
  /* No safe-area inset here: this bar is NOT the bottom-most element — the
     station's <BottomTabs/> sits below it and already pads by --safe-bottom
     (BottomTabs.js:29). F15 added env(safe-area-inset-bottom) to both, which
     double-counted the home indicator and left ~34px of dead space under the
     total, reading as an empty panel. */
  min-height:64px;padding:10px 14px;
  border-top:1px solid color-mix(in srgb, var(--c-border) 45%, transparent);
  text-align:start;cursor:pointer;
  /* Material comes from the shared .aq-liquid class (AuroraBackground.js) —
     this bar is chrome the product grid scrolls under, so it uses the same
     recipe as the station's top and bottom bars rather than its own blur. */}
.slf-cb-count{flex:none;min-width:34px;height:34px;padding:0 9px;border-radius:11px;
  background:var(--c-green);color:#fff;font-size:14px;font-weight:800;
  display:flex;align-items:center;justify-content:center;font-variant-numeric:tabular-nums;}
.slf-cb-lines{flex:1;min-width:0;display:flex;flex-direction:column;gap:1px;}
.slf-cb-l1{font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;
  color:var(--c-muted);}
.slf-cb-l2{font-size:18px;font-weight:800;letter-spacing:-.02em;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}
.slf-cb-go{flex:none;min-height:44px;padding:0 16px;border-radius:13px;
  background:var(--c-green);color:#fff;font-size:14px;font-weight:800;
  display:flex;align-items:center;gap:8px;}

/* z-index 5/6 deliberately, NOT the 40s. .slf-root is position:relative with
   z-index:auto, so it creates no stacking context and these compete directly
   with the body-level portals around them: CartPanel's promo scrim/sheet
   (40/50), OrdersPanelNative (44/45) and TopBarExtras' menu (59/60). Every
   one of those must paint ABOVE this sheet — a promo picker opened from
   inside it has to dim it, not sit on an undimmed one. Nothing else inside
   the surface uses z-index, so 5/6 is enough to cover the stage. */
.slf-scrim{position:absolute;inset:0;z-index:5;
  background:color-mix(in srgb, var(--c-ink) 42%, transparent);
  animation:slf-scrim-in .2s ease;}
.slf-sheet{position:absolute;left:0;right:0;bottom:0;z-index:6;
  height:86%;max-height:86%;display:flex;flex-direction:column;overflow:hidden;
  background:var(--c-surface);border-radius:22px 22px 0 0;
  border-top:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);
  box-shadow:0 -18px 50px color-mix(in srgb, var(--c-ink) 22%, transparent);
  animation:slf-sheet-in .28s cubic-bezier(.32,.72,0,1);}
@keyframes slf-scrim-in{from{opacity:0;}to{opacity:1;}}
@keyframes slf-sheet-in{from{transform:translateY(100%);}to{transform:translateY(0);}}
/* Legacy animates this sheet and ships a motion-reduce fallback
   (CashierSalesPage.js:2997,3005); keep that courtesy here. */
@media (prefers-reduced-motion: reduce){
  .slf-scrim,.slf-sheet{animation:none;}
}
.slf-grab{flex:none;width:100%;min-height:44px;display:flex;align-items:center;
  justify-content:center;cursor:pointer;}
.slf-grab i{display:block;width:38px;height:4px;border-radius:999px;
  background:color-mix(in srgb, var(--c-border) 90%, transparent);}
.slf-sheet-body{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}

`;

const LockIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
);

export default function SellSurface({
    ordersTabActive = false,
    onRequestOrdersTab,
    onRequestSellTab,
    // User-QC finding D — CashierStation passes its `shiftStartedAt` state
    // here, which is set fresh on EVERY successful unlock (handleUnlock),
    // even a repeat unlock by the same operator. Additive/optional: legacy
    // callers (none render SellSurface) and existing tests that don't pass
    // it are unaffected — see the effect below.
    operatorUnlockedAt = null,
    // task-n8 — CashierStation's useOperatorSession.lock, threaded down so
    // the Start-of-Day screen (rendered below) can offer a Back affordance
    // that returns to the PIN lock screen instead of trapping the operator
    // on SoD with no way out. Additive/optional: undefined here means
    // StartOfDayScreen renders with its own onBack defaulted off (no visual
    // change) — see that component's header comment.
    onLock,
} = {}) {
    const {
        tenantConfig, getTenantSetting, getCurrency, getBrandName,
        employeeId, username, authType, tenantId, logout,
    } = useAuth();
    const {
        t, language, translationDetails,
        createReceiptTranslator, dynamicTranslations, translationsLoaded,
    } = useLanguage();
    const currency = getCurrency();
    const brandName = getBrandName();

    const {
        diningOptions, diningOptionObjects, fullDiningOptions, availableTableNumbers,
        selectedDiningOption, setSelectedDiningOption,
        tableNumber, setTableNumber,
        tableNumberConfirmed, setTableNumberConfirmed,
        tableViewMode, setTableViewMode,
        loadDiningOptions, refreshDiningOptions,
    } = useDiningSetup(tenantConfig);

    const {
        isBusinessDay, setIsBusinessDay,
        businessDaySalesDate, setBusinessDaySalesDate,
    } = useBusinessDay();

    // Faithful copies of the legacy service-charge normalisation
    // (CashierSalesPage.js:343-378) — pending orders saved without a service
    // charge on a service-charge dining option are recalculated on load.
    const requiresServiceCharge = (diningOption) => {
        const optionObj = diningOptionObjects?.[diningOption];
        return optionObj?.service_charge_applied === true;
    };
    const recalculateOrderWithServiceCharge = (order) => {
        if (!order || !order.products) return order;
        const shouldHaveServiceCharge = requiresServiceCharge(order.diningOption);
        if (shouldHaveServiceCharge && !order.addServiceCharge) {
            const subtotal = order.products.reduce((total, product) => {
                const productTotal = (parseFloat(product.selling_price) || 0) * (parseInt(product.quantity) || 1);
                const addonTotal = product.add_ons
                    ? product.add_ons.reduce((sum, addon) => sum + ((parseFloat(addon.price) || 0) * (parseInt(product.quantity) || 1)), 0)
                    : 0;
                return total + productTotal + addonTotal;
            }, 0);
            const serviceCharge = subtotal * (tenantConfig?.service_charge_rate || 0.12);
            const newTotal = Math.max(0, subtotal + serviceCharge - (order.discount || 0) - (order.promoDiscountAmount || 0));
            return { ...order, addServiceCharge: true, serviceCharge, subtotal, total: newTotal };
        }
        return order;
    };

    // Pending orders live HERE (not in TableSelect) — the Task 6 orders
    // panel and charge flows read the same instance.
    const {
        pendingOrders, setPendingOrders,
        pendingOrderIds, setPendingOrderIds,
        printedOrders, setPrintedOrders,
    } = usePendingOrders(isBusinessDay, businessDaySalesDate, recalculateOrderWithServiceCharge);

    const eodMode = getTenantSetting('eod_mode', 'per_store');
    const {
        activeSession, showSoD, showEoD, setShowEoD, sessionLoading,
        handleSessionStarted, handleEndSession, handleSessionClosed,
        refreshSession,
    } = useSessionManager({ employeeId, eodMode, setIsBusinessDay, setBusinessDaySalesDate, logout });

    // User-QC finding D — re-validate against the backend on every operator
    // unlock instead of trusting the cached `activeSession`. useSessionManager's
    // own effect only re-checks when `employeeId` CHANGES, so a same-employee
    // repeat unlock (lock mid-shift, unlock again) never re-fires it — if the
    // backend had closed the session while locked, the stale cached session
    // would silently resume. Skip the very first firing: SellSurface only
    // mounts once `operatorUnlockedAt` is already set (first unlock), and
    // useSessionManager's own mount-time effect already covers that initial
    // check — this effect exists purely for SUBSEQUENT unlocks.
    const skippedFirstUnlockRef = useRef(true);
    useEffect(() => {
        if (skippedFirstUnlockRef.current) {
            skippedFirstUnlockRef.current = false;
            return;
        }
        if (operatorUnlockedAt && typeof refreshSession === 'function') {
            refreshSession();
        }
    }, [operatorUnlockedAt, refreshSession]);

    // SellSurface owns the shared catalog + builder instances so CatalogBrowse
    // (Task 2) and the cart panel (Task 3) work off the same state.
    const catalog = useProductCatalog(tenantConfig);
    const [error, setError] = useState('');
    const [pillNudge, setPillNudge] = useState(false);
    const builder = useOrderBuilder({
        tenantConfig,
        selectedDiningOption,
        diningOptionObjects,
        bulkCalculatedStock: catalog.bulkCalculatedStock,
        products: catalog.products,
        setError,
        t,
    });

    // Promotion evaluation — page-level, right after the builder, exactly
    // like legacy (CashierSalesPage.js:215-230). Owned here so the Task 5
    // charge path composes the same applied promo the cart displays.
    const promo = usePromoEvaluator({
        selectedProducts: builder.selectedProducts,
        selectedDiningOption,
        subtotal: builder.memoizedTotals.subtotal,
        enabled: !!tenantConfig,
    });

    // ---- F15: phone layout ------------------------------------------------
    // Below 768px the cart cannot share the stage row (see the CSS note on
    // .slf-cartbar), so it moves into a slide-up sheet reached from a
    // persistent bottom bar. Above the breakpoint both are inert and the
    // tablet layout is exactly as it was.
    const isMobile = useIsMobile();
    const [mobileCartOpen, setMobileCartOpen] = useState(false);

    // ---- Task 5: charge-path state (legacy CashierSalesPage.js:93-124) ---
    const [success, setSuccess] = useState('');
    const [transactions, setTransactions] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);

    // F15 part 2 — the phone till is a deliberately REDUCED second till, not a
    // shrunken tablet. "Split Payment" hands off to SplitBillManager, a
    // genuinely multi-column tool (1,500 lines of per-guest line assignment)
    // that cannot be made usable at 390px; offering it on a phone only leads a
    // cashier into a dead end mid-sale. Filtered at the point the CHOICE is
    // offered rather than inside PaymentConfirmModal, which is reused verbatim
    // by the legacy web page and must keep behaving as it does there.
    // SplitBillManager itself still receives the unfiltered list — it is only
    // reachable from the tablet, and it needs every method to settle with.
    const offeredPaymentMethods = useMemo(
        () => (isMobile ? paymentMethods.filter((m) => m !== 'Split Payment') : paymentMethods),
        [isMobile, paymentMethods],
    );
    // Page-level "confirmed" payment method (legacy 94) — only ever set by
    // the pending-order confirm-modal / resetAllFormState (both Task 6), so
    // it stays null through the whole Task 5 no-table flow; kept as real
    // state (not aliased to selectedModalPaymentMethod) so SplitBillManager
    // receives the exact same — mostly-unset — value legacy passes it.
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [selectedModalPaymentMethod, setSelectedModalPaymentMethod] = useState(null);
    const [paymentConfirmModalOpen, setPaymentConfirmModalOpen] = useState(false);
    const [pendingTransaction, setPendingTransaction] = useState(null);
    // Table-service (settle-from-pending / split) charging is Task 6 — kept
    // here, always null, so the reused PaymentConfirmModal/SplitBillManager
    // are wired with the same prop shape legacy composes them with.
    const [currentConfirmOrder, setCurrentConfirmOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [printingEnabled, setPrintingEnabled] = useState(true);
    const [receiptTranslationsEnabled, setReceiptTranslationsEnabled] = useState(true);
    const [receiptTranslationLanguage, setReceiptTranslationLanguage] = useState('');
    const [configLoaded, setConfigLoaded] = useState(false);
    const splitBillRef = useRef(null);

    // ---- Task 6: pending orders (hold/edit/delete/settle) state ----------
    // editingOrderKey/currentOrderId mirror legacy CashierSalesPage.js:111,120.
    const [editingOrderKey, setEditingOrderKey] = useState(null);
    const [currentOrderId, setCurrentOrderId] = useState(null);
    // Order-editing protection (legacy 115-118).
    const [editingProtectionModalOpen, setEditingProtectionModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const [pendingActionData, setPendingActionData] = useState(null);
    // Which order (if any) to land the Orders tab's detail view on directly
    // (existing-table tap in TableSelect, bill-notification "View order") —
    // the contract Task 4's handleOpenOrder stub promised. Task N finding 3:
    // there is no more local "is the panel open" flag — visibility is owned
    // by CashierStation's active tab (`ordersTabActive` prop).
    const [ordersPanelInitialKey, setOrdersPanelInitialKey] = useState(null);
    // Change-table modal state (legacy 131-134).
    const [changeTableModalOpen, setChangeTableModalOpen] = useState(false);
    const [orderToChangeTable, setOrderToChangeTable] = useState(null);
    const [newTableNumber, setNewTableNumber] = useState('');
    const [newDiningOption, setNewDiningOption] = useState('');
    // Task 7 — More-menu "Staff Allowance": externalOpen pulse for the
    // REUSED StaffAllowanceManager (legacy CashierSalesPage.js:151 + 2347).
    const [openStaffAllowance, setOpenStaffAllowance] = useState(false);

    // Task 9 — More-menu "Open Drawer": gate-then-act, same shape as
    // CatalogBrowse's reorder-menu gate (a plain open/close boolean, not a
    // pulse — there is no always-mounted child component here that needs
    // re-triggering, so ManagerGate is rendered inline below and driven
    // directly by this flag).
    const [openDrawerGateOpen, setOpenDrawerGateOpen] = useState(false);

    // Cashier attribution — legacy useCashierDetection composition, gated on
    // configLoaded exactly like the page (CashierSalesPage.js:421 fetch order
    // + hook's own `if (configLoaded)` guard).
    const { cashierName } = useCashierDetection({ authType, employeeId, username, configLoaded });

    // Payment methods + printing/receipt-translation preferences — faithful
    // to legacy's init effect (CashierSalesPage.js:429-464).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const methods = await paymentMethodService.getAll();
                if (!cancelled) setPaymentMethods(methods.filter((m) => m.is_active).map((m) => m.name));
            } catch (e) {
                console.error('Failed to fetch payment methods, falling back to config:', e);
                if (!cancelled) setPaymentMethods(tenantConfig?.payment_methods || []);
            }
            try {
                const [printingPref, translationsPref, receiptLangPref] = await Promise.all([
                    tenantStorage.getState('printingEnabled'),
                    tenantStorage.getState('receiptTranslationsEnabled'),
                    tenantStorage.getState('receiptTranslationLanguage'),
                ]);
                if (cancelled) return;
                setPrintingEnabled(printingPref !== false); // Default to true if not set
                setReceiptTranslationsEnabled(translationsPref !== false); // Default to true if not set

                let receiptLang = receiptLangPref;
                if (!receiptLang && tenantConfig?.languages && tenantConfig.languages.length > 1) {
                    receiptLang = tenantConfig.languages.find((lang) => lang !== 'en') || tenantConfig.languages[1];
                }
                setReceiptTranslationLanguage(receiptLang || '');
            } catch (e) {
                console.error('Failed to load printing/translation preferences:', e);
            } finally {
                if (!cancelled) setConfigLoaded(true);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ---- Task 7: More-menu toggles + top-bar publish ---------------------
    // Faithful copy of legacy handlePrintingToggle (CashierSalesPage.js:
    // 719-729) — same tenantStorage key ('printingEnabled') and semantics.
    const handlePrintingToggle = async (enabled) => {
        try {
            setPrintingEnabled(enabled);
            await tenantStorage.setState('printingEnabled', enabled);
        } catch (err) {
            console.error('Error saving printing preference:', err);
            setError(t('failed_to_save_printing_preference'));
        }
    };

    // Same keys as legacy ('receiptTranslationsEnabled',
    // 'receiptTranslationLanguage'), but the language is now DERIVED from the
    // tenant's own language set rather than read back out of localStorage.
    //
    // The old version re-synced only `if (storedLang)`. Flip the toggle before
    // LanguageContext had written that key — a real ordering, not a
    // hypothetical — and it copied nothing, leaving translations "on" with no
    // language: a silent no-op with nothing at the till to show for it
    // (Notion item 14). Deriving removes that state rather than reporting it.
    const handleReceiptTranslationsToggle = async (enabled) => {
        try {
            setReceiptTranslationsEnabled(enabled);
            await tenantStorage.setState('receiptTranslationsEnabled', enabled);
            if (enabled) {
                const resolved = resolveReceiptLanguage(
                    tenantConfig?.languages,
                    localStorage.getItem('receiptTranslationLanguage'),
                );
                if (resolved && resolved !== receiptTranslationLanguage) {
                    setReceiptTranslationLanguage(resolved);
                    localStorage.setItem('receiptTranslationLanguage', resolved);
                    await tenantStorage.setState('receiptTranslationLanguage', resolved);
                }
            }
        } catch (err) {
            console.error('Error saving receipt translations preference:', err);
            setError(t('failed_to_save_receipt_preference'));
        }
    };

    // Session-pill date — the dateStr piece of legacy businessDayInfo
    // (CashierSalesPage.js:1771-1788); the pill itself shows
    // `session_id · dateStr` per the mockup (.tb-chip "CS-001 · Wed 16 Jul").
    const sessionDateStr = (() => {
        if (!isBusinessDay || !businessDaySalesDate) return '';
        try {
            const parts = (businessDaySalesDate || '').split(' ');
            const d = new Date(parts[0] + 'T00:00:00');
            const datePart = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
            const timePart = parts[1] ? parts[1].substring(0, 5) : '';
            return timePart ? `${datePart} · ${timePart}` : datePart;
        } catch {
            return businessDaySalesDate;
        }
    })();

    // Task 9 — manual "Open Drawer" approved: fire the pulse and report the
    // outcome. Opening a till with no sale attached is the classic route for
    // cash to walk, so this is only ever reachable from ManagerGate's
    // onApprove below — never directly from the menu click.
    const handleDrawerApproved = async (approval) => {
        setOpenDrawerGateOpen(false);
        const result = await kickDrawerManually({
            managerApprovalId: approval.id,
            requestedByEmployeeId: employeeId,
            salesDate: businessDaySalesDate,
        });
        if (result.success) setSuccess(t('drawer_opened'));
        else setError(t('drawer_open_failed'));
    };

    // Action callbacks the top-bar extras invoke — latched on a stable ref
    // (identity never changes) so publishing can't loop; see TopBarExtras.js.
    const extrasApiRef = useRef({});
    extrasApiRef.current = {
        // Task N finding 3 — the top-bar Orders button (and this `openOrders`
        // entry) is REMOVED: opening Orders now only happens via the bottom
        // tab (CashierStation) or handleOpenOrder below (which requests that
        // tab). See TopBarExtras.js's header note.
        // Legacy pulse trick (2347): set → 100ms later clear, so the SAME
        // menu item can re-open the manager after it is closed.
        openStaffAllowance: () => {
            setOpenStaffAllowance(true);
            setTimeout(() => setOpenStaffAllowance(false), 100);
        },
        togglePrinting: handlePrintingToggle,
        toggleReceiptTranslations: handleReceiptTranslationsToggle,
        endSession: handleEndSession,
        openDrawer: () => setOpenDrawerGateOpen(true),
    };
    usePublishSellExtras({
        // Gate states (SoD / EOD / locked / loading) hide the extras —
        // mirrors legacy, whose header cluster only exists on the main render.
        ready: !sessionLoading && isBusinessDay
            && !(showSoD && !activeSession) && !(showEoD && activeSession),
        sessionId: activeSession?.session_id || '',
        sessionDate: sessionDateStr,
        cashierName,
        ordersCount: Object.keys(pendingOrders || {}).length,
        printingEnabled,
        receiptTranslationsEnabled,
        // A single-language tenant has nothing to translate INTO, so the
        // toggle is hidden rather than shown-and-dead. Published (not
        // recomputed in TopBarExtras) because tenantConfig lives here.
        canTranslateReceipts: canTranslateReceipts(tenantConfig?.languages),
        receiptTranslationLanguage,
        configLoaded,
        api: extrasApiRef,
    });

    // The More menu's open/closed state is chrome owned by TopBarExtras but
    // surfaced here (via the SellExtrasContext bridge) so the Escape
    // priority chain below can rank and close it — see that effect's
    // comment for why this replaced TopBarExtras' own Escape listener.
    const { menuOpen: moreMenuOpen, setMenuOpen: setMoreMenuOpen } = useSellExtrasMenu();

    // legacy loadTransactions/reloadTransactions (CashierSalesPage.js:537-560)
    // — processTransactionWithPayment's Step 7 awaits this after every charge.
    // No transactions list is rendered on the native surface yet, so this
    // only keeps `transactions` warm for when one lands; it also refreshes
    // the stock cache (Task 2 note) so CatalogBrowse's stock badges reflect
    // the deduction the just-created transaction caused server-side.
    const loadTransactions = async () => {
        try {
            const isBizDay = await tenantStorage.getState('isBusinessDay');
            const salesDate = await tenantStorage.getState('salesDate');
            if (isBizDay && salesDate) {
                try {
                    const transactionData = await transactionService.getTransactionsAfterStartOfDay(salesDate);
                    setTransactions(transactionData);
                } catch (err) {
                    console.error('Failed to load by sales_date with time filter:', err);
                    setError(t('failed_to_load_transactions'));
                }
            } else {
                setTransactions([]);
            }
        } catch (err) {
            setError('Failed to load transactions');
            console.error(err);
        }
        await catalog.refreshBulkStock();
    };

    // Charge — legacy handleSubmitSale's validation gates (881-915) +
    // handleImmediateSaleCreation (1052-1095). Table-service options route to
    // the pending-order flow in legacy; that flow (Hold/settle) is Task 6, so
    // charging one here is guarded off rather than half-implemented.
    const handleCharge = async () => {
        if (!isBusinessDay || !businessDaySalesDate) {
            setError(t('sales_not_allowed_start_business_day'));
            return;
        }
        if (!cashierName) {
            setError(t('please_select_cashier'));
            return;
        }
        if (builder.selectedProducts.length === 0) {
            setError(t('please_add_at_least_one_product'));
            return;
        }
        if (!selectedDiningOption) {
            setError(t('please_select_dining_option'));
            return;
        }
        // Charging a table/tent order is the client's NORMAL case: pay at the
        // counter, food runs to the tent number, no waiter (client request
        // 2026-08-26). This used to bail with `charge_requires_hold_first` —
        // an unimplemented stub, not a decision. All that is required is the
        // tent number itself, which is how the runner finds the customer.
        const diningConfig = availableTableNumbers[selectedDiningOption];
        const requiresTableNumberForCharge = diningConfig != null && diningConfig !== '';
        if (requiresTableNumberForCharge && !tableNumber) {
            setError(t('please_enter_table_number', { option: selectedDiningOption }));
            return;
        }

        try {
            const transaction = await buildTransaction({
                selectedProducts: builder.selectedProducts,
                totals: builder.memoizedTotals,
                selectedDiningOption,
                tableNumber,
                tenantId,
                notes: builder.notes,
                addServiceCharge: builder.addServiceCharge,
                discountAmount: builder.discountAmount,
                appliedPromo: promo.appliedPromo,
                promoDiscountAmount: promo.promoDiscountAmount,
                businessDaySalesDate,
                cashierName,
            });
            setPendingTransaction(transaction);
            setPaymentConfirmModalOpen(true);
        } catch (err) {
            console.error('Failed to create immediate sale:', err);
            setError('Failed to prepare sale transaction. Please try again.');
        }
    };

    // Confirm — legacy processTransactionWithPayment (1231-1486), faithfully
    // extracted to lib/transactionFlow.js. `ctx` bundles every value/setter
    // the legacy closure captured.
    // After a table/tent order is charged, the food still has to reach the
    // customer — so the settled order is written into the pending store
    // flagged `paid` and stays there as a hand-over queue until the cashier
    // taps Clear (client request 2026-08-26, spec §4.2).
    //
    // processTransactionWithPayment resets the dining option, table number
    // and cart through the setters in its ctx, but this closure captured
    // those values at render time, so reading them after the await still
    // yields the order that was just charged rather than the cleared state.
    //
    // Failure here must not surface as a payment error — the money is already
    // banked; the worst case is an order missing from the hand-over queue,
    // which the cashier can still see on the KDS.
    const recordPaidTableOrder = async (transaction, paymentMethod) => {
        // `transaction` is processTransactionWithPayment's return value: the
        // created transaction, or null when nothing was banked — a failed
        // charge, or a Split Payment that has only been handed off to
        // SplitBillManager and not yet collected. Recording a paid order in
        // either case would flag the order paid, remove its Charge button and
        // send food out with no money taken.
        if (!transaction?.transaction_id) return;

        const diningConfig = availableTableNumbers[selectedDiningOption];
        const isTableOrder = diningConfig != null && diningConfig !== '';
        if (!isTableOrder || !tableNumber) return;

        try {
            await createPaidOrder({
                selectedDiningOption,
                tableNumber,
                setPendingOrders,
                setPendingOrderIds,
                setPrintedOrders,
                selectedProducts: builder.selectedProducts,
                cashierName,
                businessDaySalesDate,
                totals: builder.memoizedTotals,
                discountAmount: builder.discountAmount,
                addServiceCharge: builder.addServiceCharge,
                addTax: builder.addTax,
                notes: builder.notes,
                appliedPromo: promo.appliedPromo,
                promoDiscountAmount: promo.promoDiscountAmount,
                // The REAL backend-minted id, from the returned
                // transaction. NOT pendingTransaction, whose transaction_id
                // is always '' (buildTransaction leaves it for the backend) —
                // reading it there left every paid order unvoidable, because
                // deletePaidOrderAction refuses to clear an order it cannot
                // reverse.
                transactionId: transaction.transaction_id,
                // The method actually chosen at the modal — NOT the
                // selectedPaymentMethod state, which is not set on this path
                // and would record null on every paid order.
                paymentMethod: paymentMethod || selectedPaymentMethod || '',
            });
        } catch (err) {
            console.error('Failed to record paid order for hand-over:', err);
        }
    };

    const handleProcessPayment = async (method) => {
        const result = await processTransactionWithPayment(method, {
        t,
        pendingTransaction,
        tenantId,
        currentConfirmOrder,
        selectedDiningOption,
        tableNumber,
        businessDaySalesDate,
        selectedProducts: builder.selectedProducts,
        cashierName,
        printableCategories: catalog.printableCategories,
        notes: builder.notes,
        pendingOrders,
        pendingOrderIds,
        brandName,
        currency,
        tenantConfig,
        receiptTranslationLanguage,
        createReceiptTranslator,
        dynamicTranslations,
        translationsLoaded,
        printingEnabled,
        receiptTranslationsEnabled,
        appliedPromo: promo.appliedPromo,
        splitBillRef,
        setPaymentConfirmModalOpen,
        setSelectedModalPaymentMethod,
        setLoading,
        setError,
        setSuccess,
        setPendingTransaction,
        setPendingOrders,
        setPrintedOrders,
        setCurrentConfirmOrder,
        setSelectedProducts: builder.setSelectedProducts,
        setNotes: builder.setNotes,
        setDiscountType: builder.setDiscountType,
        setDiscountPercentage: builder.setDiscountPercentage,
        setDiscountAmount: builder.setDiscountAmount,
        setPayableAmount: builder.setPayableAmount,
        setSelectedCategory: catalog.setSelectedCategory,
        setSelectedSubcategory: catalog.setSelectedSubcategory,
        setSelectedDiningOption,
        setTableNumber,
        setTableNumberConfirmed,
        setAddServiceCharge: builder.setAddServiceCharge,
        setAddTax: builder.setAddTax,
        setDiscountMode: builder.setDiscountMode,
            resetPromo: promo.resetPromo,
            loadTransactions,
        });

        await recordPaidTableOrder(result, method);
        return result;
    };

    useEffect(() => {
        loadDiningOptions();
        catalog.loadCatalog().catch((err) => console.error('Failed to load catalog:', err));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Feed the modifier catalog once on mount (Task 17 — without this the
    // builder's modifierCatalog stays at its empty useState default forever
    // and every product falls back to the legacy flat add-on path).
    // modifierService.getCatalog() already routes online/offline itself,
    // exactly like catalog.loadCatalog() above.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const modifierCatalog = await modifierService.getCatalog();
                if (!cancelled) builder.setModifierCatalog(modifierCatalog);
            } catch (err) {
                console.error('Failed to load modifier catalog:', err);
                // Leave the catalog empty — products fall back to legacy
                // add-ons rather than the till breaking.
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Active occupations poll (10s) + bill-request detection — faithful to
    // the legacy effect (CashierSalesPage.js:497-534): seenBillRequests
    // diffs new bill_requested_at flags into banner notifications, drops
    // seen ids when the flag clears, and prunes stale (cleared/paid-away)
    // occupations. Interval is cleared on unmount; polling pauses while the
    // document is hidden.
    const [activeOccupations, setActiveOccupations] = useState([]);
    const [billNotifications, setBillNotifications] = useState([]);
    const seenBillRequests = useRef(new Set());
    useEffect(() => {
        const fetchOccs = async () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
            try {
                const occs = await occupationService.listActive();
                setActiveOccupations(occs);
                const newNotifs = [];
                const activeIds = new Set(occs.map((o) => o.id));
                occs.forEach((occ) => {
                    if (occ.bill_requested_at && !seenBillRequests.current.has(occ.id)) {
                        seenBillRequests.current.add(occ.id);
                        newNotifs.push({
                            id: occ.id,
                            tableNumber: occ.table_number,
                            diningOptionId: occ.dining_option_id,
                            guestName: occ.guest_name || occ.guest?.name,
                            time: new Date(),
                        });
                    }
                    if (!occ.bill_requested_at && seenBillRequests.current.has(occ.id)) {
                        seenBillRequests.current.delete(occ.id);
                    }
                });
                // Remove notifications for occupations that no longer exist (cleared/paid)
                const staleIds = [...seenBillRequests.current].filter((id) => !activeIds.has(id));
                staleIds.forEach((id) => seenBillRequests.current.delete(id));
                if (staleIds.length > 0) {
                    setBillNotifications((prev) => prev.filter((n) => !staleIds.includes(n.id)));
                }
                if (newNotifs.length > 0) {
                    setBillNotifications((prev) => [...newNotifs, ...prev]);
                }
            } catch {}
        };
        fetchOccs();
        const interval = setInterval(fetchOccs, 10000);
        return () => clearInterval(interval);
    }, []);

    // Opens the native Orders panel directly onto an order's detail — the
    // contract TableSelect and the bill banners already call with
    // (orderKey, order); fulfils the Task 4 stub. Task N finding 3: this now
    // ROUTES TO THE SAME SURFACE the bottom Orders tab shows, instead of
    // opening a separate slide-in on top of Sell — requests the tab switch
    // via the CashierStation-owned callback and seeds the detail key the
    // embedded panel picks up once `ordersTabActive` flips true.
    const handleOpenOrder = (orderKey, order) => {
        setOrdersPanelInitialKey(orderKey);
        if (typeof onRequestOrdersTab === 'function') onRequestOrdersTab();
    };

    // The current table's occupation (legacy CashierSalesPage.js:278-295 —
    // there a dedicated fetch-on-table-select effect; here derived from the
    // same 10s occupation poll TableSelect/bill-banners already read, see
    // OrdersPanelNative's header comment for the identical, documented
    // efficiency deviation). Only meaningful for Dine-In-style numbered
    // table options — feeds createPendingOrder's markFirstOrder call.
    const activeOccupation = useMemo(() => {
        if (!selectedDiningOption || !tableNumber) return null;
        if (!Array.isArray(availableTableNumbers[selectedDiningOption])) return null;
        const doId = diningOptionObjects?.[selectedDiningOption]?.id;
        if (doId == null) return null;
        return activeOccupations.find(
            (o) => String(o.dining_option_id) === String(doId) && String(o.table_number) === String(tableNumber)
        ) || null;
    }, [selectedDiningOption, tableNumber, diningOptionObjects, availableTableNumbers, activeOccupations]);

    // ---- Order-editing protection (legacy checkEditingConflict/
    // executePendingAction, CashierSalesPage.js:596-678) ------------------
    // Returns true (and opens the protection modal) when a conflicting
    // action is attempted while editingOrderKey is set for a DIFFERENT
    // order/action than the one already being edited.
    const checkEditingConflict = (actionType, actionData = null) => {
        if (!editingOrderKey) return false;
        if (actionType === 'editSameOrder' && actionData === editingOrderKey) return false;
        setPendingAction(actionType);
        setPendingActionData(actionData);
        setEditingProtectionModalOpen(true);
        return true;
    };

    const editLoadCtx = () => ({
        setSelectedProducts: builder.setSelectedProducts,
        setSelectedDiningOption,
        setTableNumber,
        setAddServiceCharge: builder.setAddServiceCharge,
        setAddTax: builder.setAddTax,
        setDiscountAmount: builder.setDiscountAmount,
        setNotes: builder.setNotes,
        setSelectedPaymentMethod,
        setEditingOrderKey,
        setCurrentOrderId,
        pendingOrderIds,
    });

    const saveEditedOrderCtx = () => ({
        editingOrderKey, pendingOrders, pendingOrderIds,
        cashierName, selectedDiningOption, tableNumber,
        selectedProducts: builder.selectedProducts, notes: builder.notes,
        selectedPaymentMethod, addServiceCharge: builder.addServiceCharge, addTax: builder.addTax,
        totals: builder.memoizedTotals, appliedPromo: promo.appliedPromo, promoDiscountAmount: promo.promoDiscountAmount,
        discountAmount: builder.discountAmount,
        setPendingOrders, setSuccess, setError, t,
        username, products: catalog.products, tenantConfig, printableCategories: catalog.printableCategories,
        resetFormState: builder.resetFormState, setEditingOrderKey,
        setSelectedCategory: catalog.setSelectedCategory, setSelectedSubcategory: catalog.setSelectedSubcategory,
        setTableNumber, setTableNumberConfirmed, setSelectedDiningOption,
        setAddServiceCharge: builder.setAddServiceCharge, setAddTax: builder.setAddTax, setDiscountMode: builder.setDiscountMode,
        brandName, createReceiptTranslator, receiptTranslationLanguage, receiptTranslationsEnabled,
        dynamicTranslations, translationsLoaded,
    });

    // legacy cancelEditingOrder, CashierSalesPage.js:1489-1498 — the ONLY
    // call site is handleDiscardAndContinue below, exactly like legacy.
    const cancelEditingOrder = () => {
        builder.resetFormState();
        setEditingOrderKey(null);
        setSelectedDiningOption(null);
        catalog.setSelectedCategory(null);
        catalog.setSelectedSubcategory(null);
        setTableNumber('');
        setTableNumberConfirmed(false);
        promo.resetPromo();
    };

    // Task 8 item 6 — the full legacy Clear-basket reset (Cart action row's
    // third button, CashierSalesPage.js:2013-2029: products + every
    // discount field + notes + service charge/tax + both expanders + the
    // applied promo — NOT just an empty cart). Legacy's own Clear button
    // never runs this through checkEditingConflict (silently discards an
    // in-progress edit); the native "Clear basket" button closes that gap by
    // routing through the guard first (see handleClearBasket below), which
    // is also what finally gives the 'clearBasket' executePendingAction
    // branch a real call site.
    const clearBasketNow = () => {
        builder.setSelectedProducts([]);
        builder.setDiscountAmount(0);
        builder.setDiscountPercentage(0);
        builder.setDiscountMode('direct');
        builder.setPayableAmount(0);
        builder.setNotes('');
        builder.setAddServiceCharge(false);
        builder.setAddTax(false);
        builder.setShowDiscountPanel(false);
        builder.setShowNotesPanel(false);
        promo.resetPromo();
    };

    // executePendingAction — legacy 646-678. `editDifferentOrder` is the
    // legacy-orphaned branch (undefined startEditPendingOrderDirect at 663)
    // that Task 6 properly wires: handleEditOrder below is its one real call
    // site, storing { orderKey, order } as pendingActionData so this branch
    // can load the other order directly (bypassing the conflict check that
    // already ran). `clearBasket` now has a real call site too (see
    // handleClearBasket below, Task 8 item 6).
    //
    // Task 8 item 11 — legacy's `submitSale` case is REMOVED here (was
    // `handleSubmitSaleDirect()`/`handleCharge()`), not carried over for
    // switch-parity: no call site in legacy OR this file EVER invokes
    // checkEditingConflict('submitSale', ...) — grep-verified against both
    // sources (legacy's only checkEditingConflict call sites are
    // 'changeDiningOption' and two 'changeTableNumber's; this file's are
    // 'editDifferentOrder', 'changeTableNumber', 'changeDiningOption', and
    // now 'clearBasket'). Charging directly commits the in-progress edit —
    // there is nothing to protect against, so unlike `editDifferentOrder`
    // this was never an orphaned bug to fix, just dead scaffolding to drop.
    const executePendingAction = () => {
        if (!pendingAction) return;
        switch (pendingAction) {
            case 'changeDiningOption':
                setSelectedDiningOption(pendingActionData);
                setTableNumber('');
                break;
            case 'changeTableNumber':
                setTableNumber(pendingActionData);
                break;
            case 'editDifferentOrder':
                loadOrderForEdit(pendingActionData.orderKey, pendingActionData.order, editLoadCtx());
                break;
            case 'clearBasket':
                clearBasketNow();
                break;
            default:
                console.warn('Unknown pending action:', pendingAction);
        }
    };

    // handleSaveAndContinue awaits the save (a correctness fix over legacy's
    // fire-and-forget `saveEditedOrder(); executePendingAction();` — see
    // report for why: without awaiting, the "load the other order" step in
    // executePendingAction could race the in-flight save and clobber its own
    // reset of the builder state).
    const handleSaveAndContinue = async () => {
        await saveEditedOrder(saveEditedOrderCtx());
        executePendingAction();
        setEditingProtectionModalOpen(false);
        setPendingAction(null);
        setPendingActionData(null);
    };
    const handleDiscardAndContinue = () => {
        cancelEditingOrder();
        executePendingAction();
        setEditingProtectionModalOpen(false);
        setPendingAction(null);
        setPendingActionData(null);
    };
    const handleCancelAction = () => {
        setEditingProtectionModalOpen(false);
        setPendingAction(null);
        setPendingActionData(null);
    };

    // Orders-panel "Edit" — allow re-entering the SAME order (no-op, already
    // loaded); guard a DIFFERENT order via checkEditingConflict/
    // editDifferentOrder; otherwise load directly (legacy TableOrderModal
    // onEdit, faithfully extracted to lib/pendingOrderFlow.loadOrderForEdit).
    const handleEditOrder = (orderKey, order) => {
        if (editingOrderKey === orderKey) return;
        if (checkEditingConflict('editDifferentOrder', { orderKey, order })) return;
        loadOrderForEdit(orderKey, order, editLoadCtx());
    };

    // Orders-panel "Table" — opens the reused ChangeTableModal for a
    // DIFFERENT order than any currently being edited (legacy never guards
    // this call site either — moving another order's table doesn't conflict
    // with editing this one).
    const handleChangeTableFromPanel = (orderKey) => {
        setOrderToChangeTable(orderKey);
        setNewTableNumber('');
        setNewDiningOption(pendingOrders[orderKey]?.diningOption || '');
        setChangeTableModalOpen(true);
    };
    const handleConfirmChangeTable = () => changeOrderTable(orderToChangeTable, newDiningOption, newTableNumber, {
        pendingOrders, pendingOrderIds, setPendingOrders, setPendingOrderIds,
        setChangeTableModalOpen, setSuccess, setError, t,
    });

    // Orders-panel "Charge" — legacy's "Confirm Payment for Pending Order"
    // per-method button (3239-3279), faithfully extracted to
    // lib/pendingOrderFlow.prepareOrderForPayment. Hands off to the SAME
    // reused PaymentConfirmModal / Task-5 processTransactionWithPayment
    // (its currentConfirmOrder branch already exists, untouched).
    const handleSettleFromPanel = async (orderKey, method) => {
        const transaction = await prepareOrderForPayment(orderKey, method, {
            t, pendingOrders, recalculateOrderWithServiceCharge, setError,
            setSelectedProducts: builder.setSelectedProducts, setSelectedPaymentMethod,
            setSelectedDiningOption, setTableNumber,
            setAddServiceCharge: builder.setAddServiceCharge, setAddTax: builder.setAddTax,
            setDiscountAmount: builder.setDiscountAmount, setNotes: builder.setNotes,
        });
        if (!transaction) return;
        setPendingTransaction(transaction);
        setCurrentConfirmOrder(orderKey);
        setPaymentConfirmModalOpen(true);
    };

    // HOLD — legacy handlePendingOrderCreation (932-1049), faithfully
    // extracted to lib/pendingOrderFlow.createPendingOrder. Guards mirror
    // handleSubmitSale's Step 0-2 (881-912); the requiresTableNumber guard
    // is the same condition CartPanel uses to show the Hold button at all,
    // kept here for defence-in-depth (never reachable via the rendered UI,
    // same as legacy's own Step 2 check for a button it also always gates).
    const handleHold = async () => {
        if (!isBusinessDay || !businessDaySalesDate) {
            setError(t('sales_not_allowed_start_business_day'));
            return;
        }
        if (!cashierName) {
            setError(t('please_select_cashier'));
            return;
        }
        if (builder.selectedProducts.length === 0) {
            setError(t('please_add_at_least_one_product'));
            return;
        }
        if (!selectedDiningOption) {
            setError(t('please_select_dining_option'));
            return;
        }
        const diningConfig = availableTableNumbers[selectedDiningOption];
        const requiresTableNumber = diningConfig != null && diningConfig !== '';
        if (requiresTableNumber && !tableNumber) {
            setError(t('please_enter_table_number', { option: selectedDiningOption }));
            return;
        }

        await createPendingOrder({
            t,
            selectedDiningOption, tableNumber, editingOrderKey,
            pendingOrders, setPendingOrders, setPendingOrderIds, setPrintedOrders,
            selectedProducts: builder.selectedProducts, cashierName, businessDaySalesDate,
            totals: builder.memoizedTotals, discountAmount: builder.discountAmount,
            addServiceCharge: builder.addServiceCharge, addTax: builder.addTax, notes: builder.notes,
            appliedPromo: promo.appliedPromo, promoDiscountAmount: promo.promoDiscountAmount,
            setError, setSuccess,
            activeOccupation,
            refreshBulkStock: catalog.refreshBulkStock,
            resetFormState: builder.resetFormState,
            setSelectedCategory: catalog.setSelectedCategory, setSelectedSubcategory: catalog.setSelectedSubcategory,
            setSelectedDiningOption, setTableNumber, setTableNumberConfirmed, resetPromo: promo.resetPromo,
            brandName, currency, printableCategories: catalog.printableCategories, tenantConfig,
            selectedPaymentMethod,
            createReceiptTranslator, receiptTranslationLanguage, receiptTranslationsEnabled,
            dynamicTranslations, translationsLoaded, printingEnabled,
        });
    };

    // Cart's primary action while editing routes to UPDATE ORDER instead of
    // an immediate sale (legacy 2090: editingOrderKey ? saveEditedOrder :
    // handleSubmitSale) — CartPanel's `onCharge` prop is the single button,
    // its label already swaps via the `editingOrderKey` prop (Task 3).
    const handleCartPrimaryAction = () => {
        if (editingOrderKey) {
            saveEditedOrder(saveEditedOrderCtx());
        } else {
            handleCharge();
        }
    };

    // CartPanel's "Clear basket" button (Task 8 item 6) — routed through the
    // same editing-conflict guard every other cart-mutating action here
    // uses, closing the gap legacy's own Clear button leaves open (see
    // clearBasketNow's header comment above).
    const handleClearBasket = () => {
        if (checkEditingConflict('clearBasket')) return;
        clearBasketNow();
    };

    // "View order" on a bill banner — legacy semantics (2539-2554): an
    // existing pending order opens directly, otherwise jump to that dining
    // option + table (browse mode) so the cashier can settle it.
    const handleBillView = (notif) => {
        const doName = Object.keys(diningOptionObjects).find(
            (name) => String(diningOptionObjects[name]?.id) === String(notif.diningOptionId)
        );
        if (doName) {
            const tableKey = `${doName}-${notif.tableNumber}`;
            if (pendingOrders[tableKey]) {
                handleOpenOrder(tableKey, pendingOrders[tableKey]);
            } else {
                setSelectedDiningOption(doName);
                setTableNumber(String(notif.tableNumber));
            }
        }
        setBillNotifications((prev) => prev.filter((n) => n.id !== notif.id));
    };

    // Add a product to the cart — legacy handleAddProduct verbatim semantics
    // (CashierSalesPage.js:707-716): the flat flow lets you browse without a
    // dining option, but adding still requires one → error + 1.2s pill pulse.
    const handleAddProduct = (product) => {
        if (!selectedDiningOption) {
            // Error auto-dismiss (Task N finding 4) is now a generic effect
            // keyed off `error` — see the effect near the render return —
            // so no per-call-site setTimeout is needed here any more.
            setError(t('select_dining_option_first') || 'Select a dining option first');
            setPillNudge(true);
            setTimeout(() => setPillNudge(false), 1200);
            return;
        }
        builder.handleProductWithAddons(product);
    };

    // 'tables' while a table-service option still needs a table (same gate as
    // the legacy page's needsTableSelection, CashierSalesPage.js:1737-1743).
    const mode = useMemo(() => {
        if (!selectedDiningOption) return 'browse';
        const tableConfig = availableTableNumbers[selectedDiningOption];
        if (Array.isArray(tableConfig) && !tableNumber) return 'tables';
        if (tableConfig === 'random_number' && !tableNumberConfirmed) return 'tables';
        return 'browse';
    }, [selectedDiningOption, availableTableNumbers, tableNumber, tableNumberConfirmed]);

    // TableSelect's `setTableNumber` prop is a single slot doing double duty
    // inside that component: the random-number entry's free-text input
    // (unguarded, exactly like legacy — checkEditingConflict is never called
    // for typed digits) AND the numbered-grid/visual tile tap on an EMPTY
    // table (legacy 2760/2784's `changeTableNumber` guard). TableSelect.js
    // is out of Task 6's file list, so the guard is applied HERE, at the
    // prop-selection boundary, only for numbered options — the one config
    // shape whose only setTableNumber caller is the guarded tile tap.
    const guardedSetTableNumber = (num) => {
        if (checkEditingConflict('changeTableNumber', num)) return;
        setTableNumber(num);
    };
    const tableSelectSetTableNumber = Array.isArray(availableTableNumbers[selectedDiningOption])
        ? guardedSetTableNumber
        : setTableNumber;

    const pillLabel = (name) =>
        getEntityTranslation(diningOptionObjects[name], language, translationDetails) || translateDynamic(name, t);

    // Select a dining option — legacy handleDiningPillSelect semantics
    // (CashierSalesPage.js:687-703): no-table options preview the NEXT daily
    // order number (read-only — the counter increments at charge time,
    // Task 5); table options clear the table so the grid gates on it.
    // Task 6: guarded by checkEditingConflict('changeDiningOption', ...)
    // (legacy 688) and now also clears category/subcategory (Task-3 parity
    // note — the legacy page clears these via its own pill handler, which
    // Task 1-3's native pill select had not yet replicated).
    const handlePillSelect = async (option) => {
        if (checkEditingConflict('changeDiningOption', option)) return;
        setSelectedDiningOption(option);
        setTableNumberConfirmed(false);
        catalog.setSelectedCategory(null);
        catalog.setSelectedSubcategory(null);
        const optConfig = availableTableNumbers[option];
        if (optConfig == null || optConfig === '') {
            const salesDate = await tenantStorage.getState('salesDate') || businessDaySalesDate;
            const counterKey = `daily_order_${option}_${salesDate}`;
            const prevCount = parseInt(await tenantStorage.getState(counterKey)) || 0;
            setTableNumber(`#${String(prevCount + 1).padStart(3, '0')}`);
        } else {
            setTableNumber('');
        }
    };

    // ---- Task 8 item 9: Escape priority chain (legacy 232-270) ----------
    // Legacy checks a flat list of page-level flags top-to-bottom and
    // `return`s on the first match. The native surface's equivalent overlay
    // set is spread across several components, each already correctly
    // self-dismissing on Escape:
    //   - PaymentConfirmModal / AddonSelectionModal / EditingProtectionModal
    //     / ChangeTableModal / DeletionConfirmModal are all REUSED, zero-edit
    //     wraps of components/common/Modal.js, which already has its own
    //     `isOpen -> Escape -> onClose` effect (same redundant page-level +
    //     Modal-level double-fire legacy itself has, not something to
    //     "fix" — Modal.js is shared far beyond this surface).
    //   - OrdersPanelNative owns pay-picker -> detail -> list -> close
    //     internally (its state is local to that component); Task 8 also
    //     closed its one real gap (deletion-confirm nested-priority — see
    //     that file).
    //   - CartPanel owns promo sheet -> discount expander -> notes expander
    //     internally (ditto — see that file's item 9 comment).
    //   - TopBarExtras' "More" dropdown (`menuOpen`, surfaced here via the
    //     SellExtrasContext bridge's `useSellExtrasMenu`) is ranked FIRST —
    //     ABOVE every overlay below, not just the selection unwind. Legacy
    //     never wired Escape to its equivalent `showMoreMenu` at all
    //     (CashierSalesPage.js has exactly one Escape listener, the one at
    //     232-270, and it has no `showMoreMenu` branch — scrim-tap-only
    //     there), so there is no legacy ranking to mirror; a transient chrome
    //     dropdown closing before any modal/panel/selection unwind is the
    //     safest default. This closes a real bug: TopBarExtras used to run
    //     its OWN `window.addEventListener('keydown', ...)` for Escape,
    //     fully independent of this chain — with both listeners un-guarded
    //     against each other, one Escape press while the menu was open AND a
    //     category/subcategory/search was selected closed BOTH layers at
    //     once, violating the "exactly one layer per press" invariant this
    //     chain exists to guarantee. Folding `menuOpen` into this single
    //     chain (and deleting TopBarExtras' own listener) restores one
    //     authority over Escape.
    // Below the menu, this listener's job is the one thing nothing else
    // owns: selection unwind (subcategory -> category -> search, mirrors
    // legacy's own trailing unwind at 253-259) — gated behind EVERY overlay
    // so Escape never jumps two layers in one press (e.g. closing the orders
    // panel AND collapsing a category chip on the same keypress), and it
    // respects the same `window.__aqanaPrintDialogOpen` guard legacy checks
    // first (251-253).
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (window.__aqanaPrintDialogOpen) return;
            if (moreMenuOpen) { setMoreMenuOpen(false); return; }
            if (ordersTabActive) return;
            if (paymentConfirmModalOpen) return;
            if (builder.addonModalOpen) return;
            if (editingProtectionModalOpen) return;
            if (changeTableModalOpen) return;
            if (promo.showPromoModal) return;
            if (builder.showDiscountPanel) return;
            if (builder.showNotesPanel) return;
            // F15: the phone cart sheet ranks below every overlay above (each
            // of which owns its own Escape) and above the selection unwind
            // below, so one press still closes exactly one layer — never the
            // sheet AND a category chip together.
            if (mobileCartOpen) { setMobileCartOpen(false); return; }

            if (catalog.selectedSubcategory) catalog.setSelectedSubcategory(null);
            else if (catalog.selectedCategory) catalog.setSelectedCategory(null);
            else if (catalog.productSearch) catalog.setProductSearch('');
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [
        moreMenuOpen, setMoreMenuOpen,
        ordersTabActive, paymentConfirmModalOpen, builder.addonModalOpen,
        editingProtectionModalOpen, changeTableModalOpen, promo.showPromoModal,
        builder.showDiscountPanel, builder.showNotesPanel, mobileCartOpen,
        catalog.selectedSubcategory, catalog.selectedCategory, catalog.productSearch,
        catalog.setSelectedSubcategory, catalog.setSelectedCategory, catalog.setProductSearch,
    ]);

    // ---- Task N finding 4: toast auto-dismiss ----------------------------
    // A single generic rule for both toasts (legacy is inconsistent:
    // 2000-3000ms depending on call site, and `charge_requires_hold_first`
    // specifically has NO timeout at all — the literal bug this closes).
    // Keyed off the message value itself: a NEW message resets the timer
    // (the effect re-runs, its cleanup clears the stale timeout first), and
    // the cleanup also covers unmount.
    useEffect(() => {
        if (!error) return undefined;
        const id = setTimeout(() => setError(''), 2500);
        return () => clearTimeout(id);
    }, [error]);
    useEffect(() => {
        if (!success) return undefined;
        const id = setTimeout(() => setSuccess(''), 2500);
        return () => clearTimeout(id);
    }, [success]);

    // ---- F15: sheet auto-close ------------------------------------------
    // Legacy closes the sheet at four separate completion sites: hold
    // (CashierSalesPage.js:1014), charge (:1456), saveEditedOrder's finally
    // (:1579) and split-payment completion (:3127). Keying off the basket
    // emptying instead covers all four, and any future path, with no call
    // site to remember: a sheet showing an empty cart is meaningless.
    // Verified equivalent — each of those paths ends at resetFormState() or
    // setSelectedProducts([]), so selectedProducts is empty in every one.
    useEffect(() => {
        if (!builder.selectedProducts.length) setMobileCartOpen(false);
    }, [builder.selectedProducts.length]);

    // Close if the viewport leaves mobile, so a rotate/resize can never strand
    // an open sheet over the restored two-pane layout (legacy 272-274).
    useEffect(() => {
        if (!isMobile) setMobileCartOpen(false);
    }, [isMobile]);

    // ---- session / business-day gating (mirrors legacy 1791-1819) ------
    if (sessionLoading) return null;

    if (showSoD && !activeSession) {
        // Task 8 item 8 — KEEP toISOString() here (NOT the app's usual
        // local-time convention): this is the exact legacy `salesDate` wire
        // format (CashierSalesPage.js's own SoD-trigger effect), which
        // `sessionService.createSession`/tenantStorage's salesDate matching
        // depends on byte-for-byte. Parity with the existing session/order
        // pipeline governs over the local-time rule for this one value.
        const now = new Date();
        const salesDate = `${now.toISOString().split('T')[0]} ${now.toTimeString().split(' ')[0]}`;
        // Task-1 Minor fix: prefer the detected cashier (matches legacy's
        // attribution everywhere else); username is the fallback while
        // useCashierDetection is still resolving (gated on configLoaded).
        const name = cashierName || username || 'Staff';
        const empId = employeeId || `ACC_${username}`;
        return (
            <div className="slf-root">
                <style>{CSS}</style>
                <div className="slf-fill">
                    <StartOfDayScreen
                        employeeId={empId}
                        employeeName={name}
                        salesDate={salesDate}
                        onSessionStarted={handleSessionStarted}
                        fitContainer
                        onBack={onLock}
                    />
                </div>
            </div>
        );
    }

    // End-of-day flow — REUSED zero-edit, filling the content slot exactly
    // like StartOfDayScreen above (legacy gate + prop set: 1809-1819).
    // onSessionClosed → handleSessionClosed → logout() → CashierStation's
    // lock-on-logout effect (tenant preserved by the capacitor logout branch).
    if (showEoD && activeSession) {
        const pendingList = Object.values(pendingOrders || {}).filter((o) => o.status !== 'completed');
        return (
            <div className="slf-root">
                <style>{CSS}</style>
                <div className="slf-fill">
                    <EndOfDayFlow
                        session={activeSession}
                        pendingOrders={pendingList}
                        onSessionClosed={handleSessionClosed}
                        onCancel={() => setShowEoD(false)}
                        // User-QC finding C — the native surface treats a 400
                        // 'Session is not open' as success-equivalent (the
                        // session is already closed server-side) so the flow
                        // completes and handleSessionClosed's logout/lock
                        // chain still runs, instead of getting stuck on a
                        // hard error for a session there's nothing left to
                        // retry. Legacy (CashierSalesPage.js) doesn't pass
                        // this — byte-identical hard-error behavior there.
                        treatAlreadyClosedAsClosed
                    />
                </div>
            </div>
        );
    }

    if (!isBusinessDay) {
        return (
            <div className="slf-root">
                <style>{CSS}</style>
                <div className="slf-locked" role="status">
                    <div className="slf-locked-ic">{LockIcon}</div>
                    <h2 className="slf-locked-h">{t('sales_interface_locked')}</h2>
                    <p className="slf-locked-p">{t('start_business_day_to_access_sales_features')}</p>
                </div>
            </div>
        );
    }

    // Task N finding 3 — the embedded panel's `onClose` fires after any
    // action that used to also close the slide-in (Edit / Change Table /
    // Settle — see OrdersPanelNative.js's existing call sites). Repurposed
    // here to mean "hand control back to Sell": clears the seeded detail key
    // and asks CashierStation to switch the active tab back to Sell so the
    // cashier sees the cart the action just loaded/affected.
    const handleOrdersPanelClose = () => {
        setOrdersPanelInitialKey(null);
        if (typeof onRequestSellTab === 'function') onRequestSellTab();
    };

    // ---- F15: the ONE cart render --------------------------------------
    // Both the tablet column and the phone sheet call this. Keeping a single
    // definition is the whole point: the props below can never be wired one
    // way on tablet and another on phone.
    const renderCartPanel = (inSheet) => (
        <CartPanel
            builder={builder}
            promo={promo}
            currency={currency}
            tenantConfig={tenantConfig}
            selectedDiningOption={selectedDiningOption}
            diningOptionObjects={diningOptionObjects}
            availableTableNumbers={availableTableNumbers}
            tableNumber={tableNumber}
            editingOrderKey={editingOrderKey}
            onCharge={handleCartPrimaryAction}
            onHold={handleHold}
            onClearBasket={handleClearBasket}
            chargeDisabled={loading}
            inSheet={inSheet}
        />
    );

    // Bar figures come from the SAME sources CartPanel uses, so the summary
    // and the panel cannot disagree: the total mirrors its totals block
    // (CartPanel.js) and the count uses the shared cartItemCount helper its
    // header badge uses. Counting selectedProducts.length here instead was a
    // real bug — two of one product read as "1" on the bar and "2 items" in
    // the sheet.
    const mobileCartCount = cartItemCount(builder.selectedProducts);
    const mobileCartTotal = Math.max(
        0,
        (builder.memoizedTotals || builder.handleCalculateTotals()).total - (promo.promoDiscountAmount || 0),
    );

    return (
        <div className="slf-root">
            <style>{CSS}</style>

            {/* Transient error/success toast row (Task N finding 4) — normal
                flow, above everything else, auto-dismissed by the effects
                above; never overlaps the pills (previously a floating
                overlay that did). */}
            {(error || success) && (
                <div className="slf-toast-row">
                    {error && <div className="slf-toast" role="alert">{error}</div>}
                    {success && <div className="slf-toast success" role="status">{success}</div>}
                </div>
            )}

            {/* Sell content — CSS-hidden (not unmounted) while the Orders tab
                is active (Task N finding 3), so catalog/table-select/cart
                state survives a tab hop, same trick CashierStation's own
                Sell pane uses. */}
            <div className="slf-sell-content" style={{ display: ordersTabActive ? 'none' : 'flex' }}>
                {/* Bill-request banner strip (legacy 2521-2568, mockup .notify) */}
                {billNotifications.map((notif) => (
                    <div key={notif.id} className="slf-notify" role="status" data-testid="bill-notify">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" />
                        </svg>
                        <span className="grow">
                            <strong>{t('table')} {notif.tableNumber}</strong>
                            {notif.guestName ? ` — ${notif.guestName}` : ''}
                            {' '}{t('requested_bill')}
                        </span>
                        <button type="button" className="slf-notify-view" onClick={() => handleBillView(notif)}>
                            {t('view_order')}
                        </button>
                        <button
                            type="button"
                            className="slf-notify-x"
                            aria-label={t('dismiss')}
                            onClick={() => setBillNotifications((prev) => prev.filter((n) => n.id !== notif.id))}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                                <path d="M18 6 6 18M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                ))}

                {/* Dining option pills. Task N round 3 finding 4 — the Floor
                    Plan pill (jump-to-first-layout-option shortcut) is
                    REMOVED by user decision (2026-07-16 QC pass): it always
                    defaulted to the first layout-carrying option's visual
                    mode with no way to reach a different one from here, and
                    legacy's own modal-with-tabs alternative wasn't wanted
                    either — not worth having either way. The List|Visual
                    toggle inside table selection (TableSelect.js) remains
                    THE floor-plan entry point; selecting a table-service
                    dining option and switching to Visual gets you there. */}
                <div className={`slf-pills${pillNudge ? ' nudge' : ''}`} role="group" aria-label={t('dining_options')}>
                    {diningOptions.map((option) => (
                        <button
                            key={option}
                            type="button"
                            className={`slf-pill${selectedDiningOption === option ? ' active' : ''}`}
                            aria-pressed={selectedDiningOption === option}
                            onClick={() => handlePillSelect(option)}
                        >
                            {pillLabel(option)}
                        </button>
                    ))}
                </div>

                <div className="slf-stage">
                    {/* Content zone — table select or catalog browse */}
                    <div className="slf-content">
                        {mode === 'tables' ? (
                            <section className="slf-tables" data-zone="tables" aria-label={t('select_table')}>
                                <TableSelect
                                    selectedDiningOption={selectedDiningOption}
                                    diningOptionObjects={diningOptionObjects}
                                    fullDiningOptions={fullDiningOptions}
                                    availableTableNumbers={availableTableNumbers}
                                    tableNumber={tableNumber}
                                    setTableNumber={tableSelectSetTableNumber}
                                    setTableNumberConfirmed={setTableNumberConfirmed}
                                    viewMode={tableViewMode}
                                    setViewMode={setTableViewMode}
                                    pendingOrders={pendingOrders}
                                    printedOrders={printedOrders}
                                    occupations={activeOccupations}
                                    currency={currency}
                                    onOpenOrder={handleOpenOrder}
                                    refreshDiningOptions={refreshDiningOptions}
                                />
                            </section>
                        ) : (
                            <section className="slf-browse" data-zone="browse" aria-label={t('products')}>
                                <CatalogBrowse
                                    catalog={catalog}
                                    builder={builder}
                                    currency={currency}
                                    onAddProduct={handleAddProduct}
                                />
                            </section>
                        )}
                    </div>

                    {/* Cart column — Task 3 order-builder panel. Task 6: editing
                        state + Hold wired to the pending-order flow; the
                        primary-action button routes to UPDATE ORDER while
                        editing (CartPanel already swaps its own label/onHold
                        visibility off editingOrderKey — Task 3 built it, no
                        CartPanel.js edits needed here).

                        F15: on phones this same call renders inside the sheet
                        below instead of in the row. ONE closure, both call
                        sites — the tablet column and the phone sheet cannot
                        drift apart. */}
                    {!isMobile && renderCartPanel(false)}
                </div>

                {/* ── F15: phone cart bar + sheet ─────────────────────────
                    Rendered in exactly the place the cart column occupied, so
                    it inherits the same visibility gating with no new
                    conditions to keep in sync. */}
                {isMobile && (
                    <>
                        <button
                            type="button"
                            className="slf-cartbar aq-liquid"
                            data-testid="mobile-cart-bar"
                            aria-expanded={mobileCartOpen}
                            aria-label={t('view_order')}
                            onClick={() => setMobileCartOpen(true)}
                        >
                            <span className="slf-cb-count">{mobileCartCount}</span>
                            <span className="slf-cb-lines">
                                <span className="slf-cb-l1">{t('current_order')}</span>
                                <span className="slf-cb-l2 money">{money(currency, mobileCartTotal)}</span>
                            </span>
                            <span className="slf-cb-go">
                                {t('view_order')}
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M18 15l-6-6-6 6" />
                                </svg>
                            </span>
                        </button>

                        {mobileCartOpen && (
                            <>
                                <div
                                    className="slf-scrim"
                                    data-testid="mobile-cart-scrim"
                                    onClick={() => setMobileCartOpen(false)}
                                />
                                <div
                                    className="slf-sheet"
                                    data-testid="mobile-cart-sheet"
                                    role="dialog"
                                    aria-modal="true"
                                    aria-label={t('current_order')}
                                >
                                    <button
                                        type="button"
                                        className="slf-grab"
                                        aria-label={t('close')}
                                        onClick={() => setMobileCartOpen(false)}
                                    >
                                        <i />
                                    </button>
                                    <div className="slf-sheet-body">
                                        {renderCartPanel(true)}
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* ── Task 6/N: pending orders — the ONE Orders surface, embedded
                full-pane in the bottom Orders tab (not a slide-in reachable
                from Sell any more — see header note). Always mounted; its
                own `isOpen` gate gives back a hidden state (renders nothing)
                while `ordersTabActive` is false, mirroring CashierStation's
                own always-mounted-but-hidden Sell pane. */}
            <OrdersPanelNative
                embedded
                isOpen={ordersTabActive}
                onClose={handleOrdersPanelClose}
                pendingOrders={pendingOrders}
                setPendingOrders={setPendingOrders}
                printedOrders={printedOrders}
                setPrintedOrders={setPrintedOrders}
                pendingOrderIds={pendingOrderIds}
                setPendingOrderIds={setPendingOrderIds}
                diningOptions={diningOptions}
                diningOptionObjects={diningOptionObjects}
                occupations={activeOccupations}
                currency={currency}
                printableCategories={catalog.printableCategories}
                paymentMethods={offeredPaymentMethods}
                tenantConfig={tenantConfig}
                brandName={brandName}
                cashierName={cashierName}
                editingOrderKey={editingOrderKey}
                initialOrderKey={ordersPanelInitialKey}
                onEditOrder={handleEditOrder}
                onChangeTable={handleChangeTableFromPanel}
                onSettle={handleSettleFromPanel}
                setSuccess={setSuccess}
                setError={setError}
            />

            {/* REUSED, zero edits — legacy CashierSalesPage.js:3166-3173 /
                3338-3388 prop composition. */}
            <EditingProtectionModal
                isOpen={editingProtectionModalOpen}
                onCancel={handleCancelAction}
                onDiscard={handleDiscardAndContinue}
                onSave={handleSaveAndContinue}
                editingOrderKey={editingOrderKey}
                pendingOrders={pendingOrders}
            />

            <ChangeTableModal
                isOpen={changeTableModalOpen}
                onClose={() => setChangeTableModalOpen(false)}
                orderToChangeTable={orderToChangeTable}
                pendingOrders={pendingOrders}
                diningOptions={diningOptions}
                availableTableNumbers={availableTableNumbers}
                newDiningOption={newDiningOption}
                setNewDiningOption={setNewDiningOption}
                newTableNumber={newTableNumber}
                setNewTableNumber={setNewTableNumber}
                onConfirm={handleConfirmChangeTable}
                confirmDisabled={!newTableNumber || !newDiningOption}
            />

            {/* ── BACKGROUND COMPONENTS — REUSED, zero edits (legacy
                CashierSalesPage.js:3064-3128 prop composition) */}
            {/* Staff Allowance — REUSED, zero edits; hideButton + externalOpen
                pulse from the More menu (legacy 2264-2276 prop set). */}
            <StaffAllowanceManager
                cashierName={cashierName}
                currency={currency}
                tenantConfig={tenantConfig}
                brandName={brandName}
                setError={setError}
                setSuccess={setSuccess}
                printingEnabled={printingEnabled}
                receiptTranslationsEnabled={receiptTranslationsEnabled}
                receiptTranslationLanguage={receiptTranslationLanguage}
                hideButton
                externalOpen={openStaffAllowance}
            />

            {/* Task 9 — manual "Open Drawer": gate-then-act, same shape as
                CatalogBrowse's reorder-menu gate. No context payload — a
                manual open has no sale or product attached to it. */}
            <ManagerGate
                open={openDrawerGateOpen}
                action="open_drawer"
                context={null}
                salesDate={businessDaySalesDate}
                onApprove={handleDrawerApproved}
                onCancel={() => setOpenDrawerGateOpen(false)}
            />

            <SplitBillManager
                ref={splitBillRef}
                pendingTransaction={pendingTransaction}
                setPendingTransaction={setPendingTransaction}
                currentConfirmOrder={currentConfirmOrder}
                setCurrentConfirmOrder={setCurrentConfirmOrder}
                pendingOrders={pendingOrders}
                setPendingOrders={setPendingOrders}
                printedOrders={printedOrders}
                setPrintedOrders={setPrintedOrders}
                pendingOrderIds={pendingOrderIds}
                cashierName={cashierName}
                selectedDiningOption={selectedDiningOption}
                selectedPaymentMethod={selectedPaymentMethod}
                tableNumber={tableNumber}
                notes={builder.notes}
                selectedProducts={builder.selectedProducts}
                setSelectedProducts={builder.setSelectedProducts}
                currency={currency}
                paymentMethods={paymentMethods}
                tenantConfig={tenantConfig}
                printableCategories={catalog.printableCategories}
                brandName={brandName}
                tenantId={tenantId}
                handleCalculateTotals={builder.handleCalculateTotals}
                loadTransactions={loadTransactions}
                setSuccess={setSuccess}
                setError={setError}
                setLoading={setLoading}
                loading={loading}
                setNotes={builder.setNotes}
                setDiscountAmount={builder.setDiscountAmount}
                setPayableAmount={builder.setPayableAmount}
                setSelectedCategory={catalog.setSelectedCategory}
                setSelectedSubcategory={catalog.setSelectedSubcategory}
                setSelectedDiningOption={setSelectedDiningOption}
                setTableNumber={setTableNumber}
                setAddServiceCharge={builder.setAddServiceCharge}
                setDiscountType={builder.setDiscountType}
                setDiscountPercentage={builder.setDiscountPercentage}
                setDiscountMode={builder.setDiscountMode}
                onSplitCompleted={() => {
                    // Reset form state after split completion — legacy 3106-3127.
                    builder.setSelectedProducts([]);
                    builder.setNotes('');
                    builder.setDiscountType('amount');
                    builder.setDiscountPercentage(0);
                    builder.setDiscountAmount(0);
                    builder.setPayableAmount(0);

                    catalog.setSelectedCategory(null);
                    catalog.setSelectedSubcategory(null);
                    setSelectedDiningOption(null);
                    setTableNumber('');
                    setTableNumberConfirmed(false);
                    builder.setAddServiceCharge(false);
                    builder.setAddTax(false);
                    builder.setDiscountMode('direct');

                    setSelectedModalPaymentMethod(null);
                }}
            />

            <PaymentConfirmModal
                isOpen={paymentConfirmModalOpen}
                onClose={() => {
                    setPaymentConfirmModalOpen(false);
                    setSelectedModalPaymentMethod(null);
                }}
                currentConfirmOrder={currentConfirmOrder}
                selectedPaymentMethod={selectedPaymentMethod}
                selectedModalPaymentMethod={selectedModalPaymentMethod}
                setSelectedModalPaymentMethod={setSelectedModalPaymentMethod}
                paymentMethods={offeredPaymentMethods}
                pendingTransaction={pendingTransaction}
                processTransactionWithPayment={handleProcessPayment}
                selectedDiningOption={selectedDiningOption}
                diningOptionObjects={diningOptionObjects}
                tableNumber={tableNumber}
                currency={currency}
            />
        </div>
    );
}
